import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { VipAndLatestAdsSkeletonLoader } from './VipAndLatestAdsSkeletonLoader';
import { AdsChunkSize } from './AdsWithInfiniteScroll.types';
import { AdsSection } from '@/components/AdsSection/AdsSection';
import { AdOrder, GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { EmptySearchResults } from '@/components/EmptySearchResults/EmptySearchResults';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { useBanner } from '@/hooks/useBanner';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { chunkArray, mergeAdsResults } from '@/utils/helpers';
import { usePreviousValue } from '@/hooks/usePreviousValue';
import { usePopState } from '@/hooks/usePopState';
import { QUERY, ROOT_CATEGORY_SLUG } from '@/constants/common';
import { useQueryParam } from '@/hooks/useQueryParam';
import { normalizeAdsChunk } from '@/components/AdsWithInfiniteScroll/VipAndLatestAdsWithInfiniteScroll.helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { useServerDataContext } from '@/contexts';
import { useResponsive } from '@/hooks/useResponsive';

export const VipAndLatestAdsWithInfiniteScroll = () => {
  const { t } = useTranslation();
  const { currentCategory: serverCurrentCategory } = useServerDataContext();
  const { isDesktop } = useResponsive();
  const { inListBanner, inListDesktopBanner, bottomBanner } = useBanner({
    isDesktop,
  });
  const { isAllCategories, currentCategory = serverCurrentCategory } =
    useCurrentCategory();
  const { isPopState, resetPopState } = usePopState();
  const { keywords, price, regionId, order, propertyOptions, userLegacyId } =
    useParseQuery();
  const source = usePlatformSource();
  const [isLoadingMore, setLoadingMore] = useState(false);

  const categoryId = currentCategory?.id;
  const filters = {
    categoryId: isAllCategories ? null : categoryId,
    price,
    regionId,
    propertyOptions,
    userLegacyId,
  };
  const [keywordsSource] = useQueryParam(QUERY.keywords_source);
  const currentUrl = typeof window !== 'undefined' && window.location.href;

  const {
    data: vipAdsData,
    loading: isVipAdsLoading,
    refetch: refetchVipAds,
    fetchMore: fetchMoreVip,
    updateQuery: updateVipAdsQuery,
  } = useQuery<GetAdsResponse>(GET_ADS(ServiceName.Vip), {
    variables: {
      orderType: AdOrder.Random,
      adKind: ServiceName.Vip,
      first: AdsChunkSize.Small,
      keywords,
      filters,
      source,
    },
    onCompleted: data =>
      normalizeAdsChunk(data, AdsChunkSize.Small, updateVipAdsQuery),
  });

  const {
    data: latestAdsData,
    loading: isLatestAdsLoading,
    refetch: refetchLatestAds,
    fetchMore: fetchMoreLatest,
    updateQuery: updateLatestAdsQuery,
  } = useQuery<GetAdsResponse>(GET_ADS(ServiceName.Latest), {
    variables: {
      orderType: order,
      first: AdsChunkSize.Large,
      keywords,
      filters,
      keywordsSource: keywordsSource?.toUpperCase(),
      sourceLink: currentUrl,
      source,
    },
    onCompleted: data =>
      normalizeAdsChunk(data, AdsChunkSize.Large, updateLatestAdsQuery),
  });

  const resetKey = JSON.stringify({ ...filters, keywords, order });

  const prevResetKey = usePreviousValue(resetKey);

  useEffect(() => {
    if (
      prevResetKey && // to avoid resetting on the first render
      prevResetKey !== resetKey &&
      !isPopState
    ) {
      refetchVipAds().then(({ data }) =>
        normalizeAdsChunk(data, AdsChunkSize.Small, updateVipAdsQuery)
      );
      refetchLatestAds().then(({ data }) =>
        normalizeAdsChunk(data, AdsChunkSize.Large, updateLatestAdsQuery)
      );
    }
    resetPopState();
  }, [resetKey]);

  const { endCursor, hasNextPage } = latestAdsData?.ads?.pageInfo || {};
  const isEmptyResults = !latestAdsData?.ads?.nodes?.some(Boolean);

  const { chunkedVipAds, chunkedLatestAds } = useMemo(() => {
    return {
      chunkedVipAds: chunkArray(
        vipAdsData?.ads?.nodes || [],
        AdsChunkSize.Small
      ),
      chunkedLatestAds: chunkArray(
        latestAdsData?.ads?.nodes || [],
        AdsChunkSize.Large
      ),
    };
  }, [vipAdsData, latestAdsData]);

  const fetchMoreVipAds = () =>
    fetchMoreVip({
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeAdsResults(prevResult, fetchMoreResult, {
          chunkSize: AdsChunkSize.Small,
        }),
    });

  const fetchMoreLatestAds = () =>
    fetchMoreLatest({
      variables: {
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeAdsResults(prevResult, fetchMoreResult, {
          chunkSize: AdsChunkSize.Large,
        }),
    });

  async function onLoadMore() {
    if (
      !isVipAdsLoading &&
      !isLatestAdsLoading &&
      !isLoadingMore &&
      hasNextPage &&
      navigator.onLine
    ) {
      setLoadingMore(true);
      await Promise.all([fetchMoreVipAds(), fetchMoreLatestAds()]);
      setLoadingMore(false);
    }
  }

  if (isVipAdsLoading || isLatestAdsLoading) {
    return <VipAndLatestAdsSkeletonLoader />;
  }

  if (isEmptyResults) {
    return <EmptySearchResults />;
  }

  return (
    <>
      {chunkedLatestAds.map((latestAdsChunk, i) => {
        const isFirstChunk = !i;
        const vipAds = chunkedVipAds[i]?.filter(Boolean);
        const latestAds = latestAdsChunk?.filter(Boolean);
        const key = `vip_and_latest_${i}`;
        const hasVipAds = vipAds?.length > 0;
        const latestAdsTitle =
          isFirstChunk || hasVipAds ? t('pages.ads.ads') : undefined;
        return (
          <Fragment key={key}>
            {!!vipAds?.length && (
              <AdsSection
                title={t('pages.ads.vip_ads')}
                ads={vipAds}
                hasPaddingTop={isFirstChunk}
                dataTestId={SearchResultsPageTestIds.vipAdsSection}
                hasFirstCardPriority
                allAdsLinkData={{
                  href: `/${currentCategory?.path || ROOT_CATEGORY_SLUG}/vip`,
                  className: 'section-title_more',
                }}
              />
            )}

            {!!latestAds?.length && (
              <AdsSection
                index={i}
                ads={latestAds}
                hasPaddingTop={isFirstChunk && !hasVipAds}
                title={latestAdsTitle}
                bannerInList={inListBanner}
                inListDesktopBanner={inListDesktopBanner}
                bottomBanner={bottomBanner}
                isBannerInListAtTheEnd
                isBannerInListAfterThreeRows
                dataTestId={SearchResultsPageTestIds.latestAdsSection}
              />
            )}
          </Fragment>
        );
      })}

      <InfiniteLoader onVisit={onLoadMore} />
    </>
  );
};
